import {
    AvatarMarkerContainer,
    RatingSlider,
    RatingThumbAvatar,
} from "../PostPollRate.styles";
import { useUserProfile } from "@infrastructure/Persistence/UserProfileState";
import { MAX_RATING, MIN_RATING } from "./PostPollRateSection";
import { UserAvatarFactory } from "../../../../UserAvatar/UserAvatarFactory";
import userAvatarChecker from "../../../../../Shared/UserAvatarChecker";
import { Box } from "@mui/system";
import { Mark } from "@mui/base/useSlider";
import { Tooltip } from "@mui/material";

export function AfterRatingSlider({
    averageRating,
    userRating = 0,
}: {
    averageRating: number;
    userRating: number | undefined;
}) {
    const { avatar, uuid, gender } = useUserProfile();

    const marks: Mark[] = [
        {
            value: averageRating,
            label: <AverageRatingMarker averageRating={averageRating} />,
        },
        {
            value: userRating,
            label: (
                <UserAvatarMarker
                    avatar={avatar}
                    uuid={uuid}
                    gender={gender}
                    scaleFactor={averageRating}
                />
            ),
        },
    ];

    return (
        <RatingSlider
            disableSwap
            disabled
            value={averageRating}
            min={MIN_RATING}
            max={MAX_RATING}
            marks={marks}
            sx={{
                ...(averageRating < MAX_RATING && {
                    " .MuiSlider-track": {
                        borderRadius: "90px 0 0 90px",
                    },
                }),
                "& .MuiSlider-thumb": {
                    display: "none",
                },
            }}
        />
    );
}

function AverageRatingMarker({ averageRating }: { averageRating: number }) {
    const { tooltipPlacement, tooltipXShift } = getTooltipXShift(averageRating);

    return (
        <Tooltip
            title={`Durchschnittliche\nAntwort ${averageRating.toFixed(1)}/10`}
            arrow
            open
            placement={tooltipPlacement}
            PopperProps={{
                disablePortal: true,
                modifiers: [
                    {
                        name: "arrow",
                        options: {
                            padding: 5,
                        },
                    },
                ],
                sx: {
                    pointerEvents: "none",
                    "& .MuiTooltip-tooltip": {
                        background: "#000000",
                        color: "var(--community-color-snow-white)",
                        maxWidth: "200px",
                        textWrap: "wrap",
                        fontSize: "12px",
                        marginTop: "0 !important",
                        transform: `translate(${tooltipXShift}, 0px) !important`,
                        textAlign: "center",
                    },
                    "& .MuiTooltip-arrow": {
                        color: "#000000",
                    },
                },
            }}
        >
            <Box
                style={{
                    height: "100%",
                    borderLeft: "1px dashed red",
                }}
            />
        </Tooltip>
    );
}

function getTooltipXShift(averageRating: number): {
    tooltipPlacement: "bottom-start" | "bottom" | "bottom-end";
    tooltipXShift: string;
} {
    switch (true) {
        case averageRating < MAX_RATING * 0.25:
            return {
                tooltipPlacement: "bottom-start",
                tooltipXShift: "-10px",
            };
        case averageRating < MAX_RATING * 0.75:
            return {
                tooltipPlacement: "bottom",
                tooltipXShift: "0px",
            };
        default:
            return {
                tooltipPlacement: "bottom-end",
                tooltipXShift: "10px",
            };
    }
}

function UserAvatarMarker({
    avatar,
    uuid,
    gender,
    scaleFactor,
}: {
    avatar: string;
    gender: string | undefined;
    scaleFactor: number;
    uuid: string | undefined;
}) {
    return (
        <AvatarMarkerContainer scaleFactor={scaleFactor}>
            {UserAvatarFactory({
                avatar: avatar,
                uuid: uuid,
                gender: gender,
                onError: () => userAvatarChecker(avatar, uuid),
                component: RatingThumbAvatar,
            })}
        </AvatarMarkerContainer>
    );
}
